import { RootState } from "@/redux/store";
import { useUpdateUserMutation } from "@/services";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { toast } from "@/components/Toast";
import { schema } from "./validations";

export const EDIT_OWNER_FORM_ID = "edit-owner-form";
/** Owner addresses are US-only (same default as the property form); Country is not editable. */
export const DEFAULT_COUNTRY = "USA";

const ADDRESS_FIELDS = ["addressLine1", "addressLine2", "pobox", "city", "state", "zipCode", "country"];

const blankToNull = (value: any) =>
  typeof value === "string" && value.trim() === "" ? null : typeof value === "string" ? value.trim() : value ?? null;

const useEditOwner = ({ user, onDone }: { user: any; onDone: () => void }) => {
  const queryClient = useQueryClient();
  const stateList = useSelector((state: RootState) => state.currentData.stateData) || [];

  // Addresses store the state name; the dropdown works on codes. Keep unknown values as-is.
  const toStateCode = (value?: string) =>
    stateList.find((s: any) => s.name == value || s.code == value)?.code || value || "";
  const toStateName = (value?: string) =>
    stateList.find((s: any) => s.code == value)?.name || value || null;

  const stateOptions = useMemo(() => {
    const options = stateList.map((s: any) => ({ label: s.name, value: s.code }));
    [user?.address?.state, user?.correspondenceAddress?.state].forEach((raw) => {
      if (raw && !options.some((o: any) => o.value == toStateCode(raw))) {
        options.push({ label: raw, value: raw });
      }
    });
    return options;
  }, [stateList, user]);

  const toFormAddress = (address: any) =>
    Object.fromEntries(
      ADDRESS_FIELDS.map((f) => [f,
        f === "state" ? toStateCode(address?.state)
          : f === "country" ? address?.country || DEFAULT_COUNTRY
          : address?.[f] || ""])
    );

  const form = useForm<any>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: "", firstName: "", lastName: "", email: "", email2: "",
      phoneNumber: "", landlineNumber: "",
      address: toFormAddress(null),
      separateCorrespondence: false,
      correspondenceAddress: toFormAddress(null),
    },
  });

  useEffect(() => {
    if (!user) return;
    form.reset({
      title: user.title || "",
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      email: user.email || "",
      email2: user.email2 || "",
      phoneNumber: user.phoneNumber || "",
      landlineNumber: user.landlineNumber || "",
      address: toFormAddress(user.address),
      separateCorrespondence: !!user.correspondenceAddress,
      correspondenceAddress: toFormAddress(user.correspondenceAddress),
    });
  }, [user, stateList]);

  const { mutate: updateUser, isPending: isSaving } = useUpdateUserMutation({
    onError: (error?: any) => {
      toast.error(
        typeof error?.response?.data?.message === "string"
          ? error?.response?.data?.message
          : "Something went wrong"
      );
    },
    onSuccess: () => {
      toast.success("Owner details updated Successfully");
      queryClient.invalidateQueries({ queryKey: ["user", String(user?.userId)] });
      queryClient.invalidateQueries({ queryKey: ["owner-master"] });
      onDone();
    },
  });

  const toRequestAddress = (address: any) => ({
    ...Object.fromEntries(ADDRESS_FIELDS.map((f) => [f, blankToNull(address?.[f])])),
    state: toStateName(address?.state),
  });

  const onSubmit = (data: any) => {
    // PUT /users/{id} copies every field (nulls included) onto the user, so fields this form
    // doesn't edit (identifiers, dob, organization) are sent back unchanged.
    const primary = toRequestAddress(data.address);
    updateUser({
      id: String(user.userId),
      data: {
        userName: user.userName,
        loginId: user.loginId,
        dob: user.dob,
        organization: user.organization,
        title: blankToNull(data.title),
        firstName: blankToNull(data.firstName),
        lastName: blankToNull(data.lastName),
        email: blankToNull(data.email),
        email2: blankToNull(data.email2),
        phoneNumber: blankToNull(data.phoneNumber),
        landlineNumber: blankToNull(data.landlineNumber),
        address: primary,
        // The API can't delete a correspondence address (null = leave as is). When "separate" is
        // unticked on an owner who has one, overwrite it with the primary address instead.
        correspondenceAddress: data.separateCorrespondence
          ? toRequestAddress(data.correspondenceAddress)
          : user.correspondenceAddress
            ? primary
            : null,
      },
    });
  };

  return { form, onSubmit, isSaving, stateOptions };
};

export default useEditOwner;
